/**
 * Recruitment Kanban Board
 * Drag-and-drop board for managing candidate pipeline
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { isHrReadOnly } from '../../../lib/hrRole';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  User, Mail, Phone, FileText, GripVertical, Plus, Search, Filter,
  Loader2, RefreshCw, ChevronRight, Briefcase, Calendar, MoreVertical
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../ui/dropdown-menu';
import { toast } from 'sonner';
import RecruitmentNav from './RecruitmentNav';
import AddCandidateDialog from './AddCandidateDialog';
import CandidateDetailDrawer from './CandidateDetailDrawer';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Stage configuration — PIPELINE-PREMIUM (Jul 22, 2026)
const STAGES = [
  { id: 'applied',   label: 'Applied',   dot: 'bg-slate-400',  bar: 'bg-slate-400',  textColor: 'text-slate-700',  countBg: 'bg-slate-100 text-slate-700',   avatar: 'bg-slate-100 text-slate-600' },
  { id: 'screening', label: 'Screening', dot: 'bg-blue-500',   bar: 'bg-blue-500',   textColor: 'text-blue-700',   countBg: 'bg-blue-50 text-blue-700',      avatar: 'bg-blue-50 text-blue-600' },
  { id: 'interview', label: 'Interview', dot: 'bg-violet-500', bar: 'bg-violet-500', textColor: 'text-violet-700', countBg: 'bg-violet-50 text-violet-700',  avatar: 'bg-violet-50 text-violet-600' },
  { id: 'offer',     label: 'Offer',     dot: 'bg-amber-500',  bar: 'bg-amber-500',  textColor: 'text-amber-700',  countBg: 'bg-amber-50 text-amber-700',    avatar: 'bg-amber-50 text-amber-600' },
  { id: 'hired',     label: 'Hired',     dot: 'bg-emerald-500',bar: 'bg-emerald-500',textColor: 'text-emerald-700',countBg: 'bg-emerald-50 text-emerald-700',avatar: 'bg-emerald-50 text-emerald-600' },
  { id: 'rejected',  label: 'Rejected',  dot: 'bg-rose-500',   bar: 'bg-rose-500',   textColor: 'text-rose-700',   countBg: 'bg-rose-50 text-rose-700',      avatar: 'bg-rose-50 text-rose-600' }
];

const fmtDate = (d) => {
  if (!d) return null;
  // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional compact kanban card date
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return null; }
};

const initialsOf = (name) => (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

// Sortable Candidate Card Component
const SortableCandidateCard = ({ candidate, stageConfig, onViewDetails }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: candidate.candidate_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  };

  const appliedDate = fmtDate(candidate.created_at);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onViewDetails(candidate)}
      className={`group bg-white rounded-lg border border-slate-200 p-3 mb-2 shadow-sm hover:shadow-md hover:border-slate-300 transition-shadow cursor-grab active:cursor-grabbing ${
        isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''
      }`}
      data-testid={`candidate-card-${candidate.candidate_id}`}
    >
      <div className="flex items-start gap-2.5">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${stageConfig.avatar}`}>
          {initialsOf(candidate.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-1">
            <p className="font-medium text-sm text-slate-900 truncate leading-tight">{candidate.name}</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 -mr-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`candidate-menu-${candidate.candidate_id}`}
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onViewDetails(candidate)}>
                  View Details
                </DropdownMenuItem>
                {candidate.resume_file_key && (
                  <DropdownMenuItem>Download Resume</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {candidate.job_title && (
            <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
              <Briefcase className="w-3 h-3 shrink-0" />
              <span className="truncate">{candidate.job_title}</span>
            </p>
          )}
          <p className="text-xs text-slate-400 truncate flex items-center gap-1 mt-0.5">
            <Mail className="w-3 h-3 shrink-0" />
            <span className="truncate">{candidate.email}</span>
          </p>
          <div className="flex items-center justify-between mt-1.5">
            {appliedDate ? (
              <span className="text-[11px] text-slate-400 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {appliedDate}
              </span>
            ) : <span />}
            {candidate.is_converted && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">Converted</Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Kanban Column Component
const KanbanColumn = ({ stage, candidates, onViewDetails }) => {
  const stageConfig = STAGES.find(s => s.id === stage) || STAGES[0];
  const { setNodeRef, isOver } = useDroppable({ id: stage });

  return (
    <div
      ref={setNodeRef}
      className={`min-w-0 flex flex-col rounded-xl bg-slate-50/80 border transition-colors ${
        isOver ? 'border-blue-400 bg-blue-50/50' : 'border-slate-200'
      }`}
      data-testid={`kanban-column-${stage}`}
    >
      <div className={`h-1 rounded-t-xl ${stageConfig.bar}`} />
      <div className="px-3 py-2.5 border-b border-slate-200/70">
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-2 h-2 rounded-full shrink-0 ${stageConfig.dot}`} />
            <h3 className={`font-semibold text-sm truncate ${stageConfig.textColor}`}>
              {stageConfig.label}
            </h3>
          </div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${stageConfig.countBg}`}>
            {candidates.length}
          </span>
        </div>
      </div>
      <div className="p-2 flex-1 min-h-[380px] max-h-[calc(100vh-380px)] overflow-y-auto">
        <SortableContext
          items={candidates.map(c => c.candidate_id)}
          strategy={verticalListSortingStrategy}
        >
          {candidates.map(candidate => (
            <SortableCandidateCard
              key={candidate.candidate_id}
              candidate={candidate}
              stageConfig={stageConfig}
              onViewDetails={onViewDetails}
            />
          ))}
        </SortableContext>
        {candidates.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-slate-300 text-xs gap-1.5 border-2 border-dashed border-slate-200 rounded-lg mx-0.5">
            <User className="w-5 h-5" />
            No candidates
          </div>
        )}
      </div>
    </div>
  );
};

// Dragging Overlay Card
const DragOverlayCard = ({ candidate }) => {
  if (!candidate) return null;
  const stageConfig = STAGES.find(s => s.id === candidate.stage) || STAGES[0];

  return (
    <div className="bg-white rounded-lg border-2 border-blue-400 p-3 shadow-2xl w-64 rotate-2">
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${stageConfig.avatar}`}>
          {initialsOf(candidate.name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-slate-900 truncate">{candidate.name}</p>
          <p className="text-xs text-slate-500 truncate">{candidate.email}</p>
        </div>
      </div>
    </div>
  );
};

const RecruitmentKanban = ({ currentUser }) => {
  const hrReadOnly = isHrReadOnly();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCandidate, setActiveCandidate] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [detailCandidateId, setDetailCandidateId] = useState(null);
  const dragBlockRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor)
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [candidatesRes, jobsRes] = await Promise.all([
        axios.get(`${API}/api/recruitment/candidates?limit=100`),
        axios.get(`${API}/api/recruitment/jobs?status=open`)
      ]);

      setCandidates(candidatesRes.data.data || []);
      setJobs(jobsRes.data.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load recruitment data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter candidates
  const filteredCandidates = candidates.filter(c => {
    const matchesJob = selectedJob === 'all' || c.job_id === selectedJob;
    const matchesSearch = !searchQuery || 
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesJob && matchesSearch;
  });

  // Group by stage
  const candidatesByStage = STAGES.reduce((acc, stage) => {
    acc[stage.id] = filteredCandidates.filter(c => c.stage === stage.id);
    return acc;
  }, {});

  const handleDragStart = (event) => {
    dragBlockRef.current = true;
    const candidate = candidates.find(c => c.candidate_id === event.active.id);
    setActiveCandidate(candidate);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveCandidate(null);
    setTimeout(() => { dragBlockRef.current = false; }, 250);

    if (hrReadOnly) return;
    if (!over) return;

    const candidateId = active.id;
    const candidate = candidates.find(c => c.candidate_id === candidateId);

    if (!candidate) return;

    let targetStage = candidate.stage;

    // Dropped directly on a column
    if (STAGES.some(s => s.id === over.id)) {
      targetStage = over.id;
    } else {
      // Dropped on another candidate — use their stage
      const overCandidate = candidates.find(c => c.candidate_id === over.id);
      if (overCandidate) targetStage = overCandidate.stage;
    }

    if (candidate.stage === targetStage) return;

    // Prevent changes to converted candidates
    if (candidate.is_converted) {
      toast.error('Cannot modify converted candidate');
      return;
    }

    setUpdating(true);
    try {
      await axios.put(`${API}/api/recruitment/candidates/${candidateId}`, {
        stage: targetStage
      });

      // Update local state
      setCandidates(prev => prev.map(c =>
        c.candidate_id === candidateId ? { ...c, stage: targetStage } : c
      ));

      const stageLabel = STAGES.find(s => s.id === targetStage)?.label || targetStage;
      toast.success(`${candidate.name} moved to ${stageLabel}`);
    } catch (err) {
      console.error('Error updating candidate:', err);
      toast.error(err.response?.data?.detail || 'Failed to update candidate');
    } finally {
      setUpdating(false);
    }
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (!over) return;

    // Update visual feedback
  };

  const handleViewDetails = (candidate) => {
    if (dragBlockRef.current) return;
    setDetailCandidateId(candidate.candidate_id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="recruitment-kanban">
      <RecruitmentNav />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <User className="w-6 h-6 text-blue-600" />
            Recruitment Pipeline
          </h2>
          <p className="text-gray-500 mt-1">Drag candidates between stages</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search candidates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-48"
              data-testid="kanban-search"
            />
          </div>
          <Select value={selectedJob} onValueChange={setSelectedJob}>
            <SelectTrigger className="w-[180px]" data-testid="job-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by job" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Jobs</SelectItem>
              {jobs.map(job => (
                <SelectItem key={job.job_id} value={job.job_id}>
                  {job.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-kanban">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {!hrReadOnly && (
            <Button onClick={() => setShowAddCandidate(true)} className="gap-2 shadow-sm" data-testid="kanban-add-candidate-btn">
              <Plus className="w-4 h-4" />
              Add Candidate
            </Button>
          )}
        </div>
      </div>

      {/* Stage KPI Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kanban-kpi-row">
        {STAGES.map(stage => (
          <div
            key={stage.id}
            className="bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm"
            data-testid={`kpi-card-${stage.id}`}
          >
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${stage.dot}`} />
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide truncate">{stage.label}</span>
            </div>
            <p className={`text-2xl font-bold mt-1 ${stage.textColor}`}>
              {(candidatesByStage[stage.id] || []).length}
            </p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-4 text-sm">
        <span className="text-gray-500">
          Total: <strong className="text-gray-900">{filteredCandidates.length}</strong> candidates
        </span>
        {updating && (
          <span className="flex items-center gap-1 text-blue-600">
            <Loader2 className="w-3 h-3 animate-spin" />
            Updating...
          </span>
        )}
      </div>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {STAGES.map(stage => (
            <KanbanColumn
              key={stage.id}
              stage={stage.id}
              candidates={candidatesByStage[stage.id] || []}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>

        <DragOverlay>
          <DragOverlayCard candidate={activeCandidate} />
        </DragOverlay>
      </DndContext>

      <AddCandidateDialog
        open={showAddCandidate}
        onOpenChange={setShowAddCandidate}
        jobs={jobs}
        onCreated={fetchData}
      />

      <CandidateDetailDrawer
        candidateId={detailCandidateId}
        navIds={STAGES.flatMap(s => (candidatesByStage[s.id] || []).map(c => c.candidate_id))}
        onNavigate={setDetailCandidateId}
        onClose={() => setDetailCandidateId(null)}
        onChanged={fetchData}
      />
    </div>
  );
};

export default RecruitmentKanban;
